import { getLogger } from '@sas/nova-core/logging';
import type { GetIconFromRegisteredType, ResolveIcon } from '@sas/react-svg';
import { Localizer } from '../../../locale/useTextLocalizer'; // DSD~ '@sas/nova-commons/locale/useTextLocalizer';
import {
  EntityTypeInformation,
  RegisteredType,
  TypeInformation,
  SearchResult,
} from '../../borrowed/catalog-app/typescript/src/app/model/types';
import { ResolveTableURI } from '../../borrowed/data-discovery-commons/client/data/resolver_fast';
/* DSD-
import { selectRegisteredTypesByName } from '../state/RegisteredTypesSelectors';
import store from '../state/store';
import { RegisteredType } from '../model/types';
*/

const logger = getLogger('DataSelectionUtilTypes');
const getIconAndTypeLabel = (options: {
  localize: Localizer;
  typeInformation: TypeInformation;
  registeredTypes?: Record<string, RegisteredType>;
  resolveIcon: ResolveIcon;
  getIconFromRegisteredType: GetIconFromRegisteredType;
}): void => {
  let iconName: string | undefined;
  let typeLabel: string | undefined;

  const displayTypeUpper = options.typeInformation.displaytype.toUpperCase();

  switch (displayTypeUpper) {
    case 'CSV':
      iconName = options.getIconFromRegisteredType('documentcsv', false)?.name;
      typeLabel = options.localize(`table.extension.csv.label`);
      break;
    case 'JMP':
      iconName = 'datatablejmp';
      typeLabel = options.localize(`table.extension.jmp.label`);
      break;
    case 'TEXT':
      iconName = options.getIconFromRegisteredType('file', false)?.name;
      typeLabel = options.localize(`table.extension.txt.label`);
      break;
    case 'STATA':
      iconName = 'datatablestata';
      typeLabel = options.localize(`table.extension.dta.label`);
      break;
    case 'SPSS':
      iconName = 'spssfile';
      typeLabel = options.localize(`table.extension.sav.label`);
      break;
    case 'MICROSOFT EXCEL':
      iconName = 'excelfile';
      typeLabel = options.localize(`table.extension.xls.label`);
      break;
    case 'PARQUET':
      iconName = 'datatableparquet';
      typeLabel = options.localize(`table.extension.parquet.label`);
      break;
    case 'ORC':
      iconName = 'datatableorc';
      typeLabel = options.localize(`table.extension.orc.label`);
      break;
    case 'CAS':
      iconName = options.getIconFromRegisteredType('castable', false)?.name;
      typeLabel = options.localize(`table.type.casTable.msg`);
      break;
    case 'SAS':
      iconName = options.getIconFromRegisteredType('sastable', false)?.name;
      typeLabel = options.localize(`table.type.sasTable.msg`);
      break;
    case 'DATATABLE':
      iconName = options.getIconFromRegisteredType('datatable', false)?.name;
      typeLabel = options.localize(`table.type.generic.msg`);
      break;
    case 'INMEMORYTABLE':
      iconName = 'InMemoryTable';
      typeLabel = options.localize('table.inmemory.msg');
      break;
    default:
      if (options.typeInformation.providerType) {
        iconName = options.resolveIcon(`datatable${displayTypeUpper}`)?.name;
        if (iconName) {
          typeLabel = options.localize(`table.source.datatable${displayTypeUpper}.msg`);
          if (typeLabel && typeLabel.startsWith('NovaComponents-DataSelection')) {
            // well that lookup didn't work, clear icon and type.
            typeLabel = undefined;
            iconName = undefined;
          }
        }
      }
      break;
  }

  if (!typeLabel) {
    iconName = options.getIconFromRegisteredType(options.typeInformation.registeredtype, false)
      ?.name;
    // only setup the type label based on registered types if we have types loaded...
    if (options.registeredTypes) {
      const registeredType: RegisteredType | undefined =
        options.registeredTypes[options.typeInformation.registeredtype.toLowerCase()];

      if (registeredType) {
        // Registered types are loaded and we have a match. Use the label (if set), otherwise the name.
        typeLabel = registeredType.label || registeredType.name;
      } else {
        // Registered types are loaded but we didn't have a match. Fallback to unlocalized displaytype.
        // SOPHIA TODO: maybe a few generics to try here before falling back to unlocalized?
        typeLabel = options.typeInformation.displaytype;
      }
    }
  }

  options.typeInformation.iconName = iconName;
  options.typeInformation.typeLabel = typeLabel || '';
};

const determineProviderType = (itemType: string): 'cas' | 'sas' | undefined => {
  return itemType === 'sasTable'
    ? 'sas'
    : itemType === 'casTable' || itemType === 'casMemTable'
    ? 'cas'
    : undefined;
};

export const setupTypeInformation = async ({
  localize,
  item,
  registeredTypes,
}: {
  localize: Localizer;
  item: {
    type: SearchResult['type'];
    attributeMap: SearchResult['attributeMap']; // Record<string,any> for catalog grpc type handling
    registeredtype?: SearchResult['registeredtype'];
    displaytype?: SearchResult['displaytype'];
    resourceuri?: SearchResult['resourceuri'];
  };
  registeredTypes?: Record<string, RegisteredType>; // DSD+
}): Promise<TypeInformation> => {
  const t = await setupTypeInformationBulk({ localize, items: [item], registeredTypes });
  return t[0];
};

export const setupTypeInformationBulk = async ({
  localize,
  items,
  registeredTypes,
}: {
  localize: Localizer;
  items: {
    type: SearchResult['type'];
    attributeMap: SearchResult['attributeMap']; // Record<string,any> for catalog grpc type handling
    registeredtype?: SearchResult['registeredtype'];
    displaytype?: SearchResult['displaytype'];
    resourceuri?: SearchResult['resourceuri'];
  }[];
  registeredTypes?: Record<string, RegisteredType>; // DSD+
}): Promise<TypeInformation[]> => {
  const { resolveIcon } = await import(
    /* webpackMode: "lazy" */ '@sas/react-svg/ejs/resolveSVGIconName'
  );
  const { getIconFromRegisteredType } = await import(
    /* webpackMode: "lazy" */
    '@sas/react-svg/ejs/getIconFromRegisteredType'
  );
  return (items || [])
    .filter((item) => !!item)
    .map((item) => {
      let registeredtype: string | undefined;
      let displaytype: string | undefined;

      if ((item as SearchResult).registeredtype && (item as SearchResult).registeredtype !== '') {
        registeredtype = (item as SearchResult).registeredtype;
      }
      if ((item as SearchResult).displaytype && (item as SearchResult).displaytype !== '') {
        displaytype = (item as SearchResult).displaytype;
      }
      const resourceUri = (item as SearchResult).resourceuri;
      const typeInformation = calculateTypeInformation({
        attributeMap: item.attributeMap,
        type: item.type,
        displaytype,
        registeredtype,
        resourceUri,
      });
      const iscrawled: boolean = typeInformation.iscrawled;

      const providerType = determineProviderType(item.type);

      const isOpenSupported: boolean = typeInformation.registeredtype === 'dataTable';

      const typeInfo: TypeInformation = {
        isOpenSupported,
        iscrawled,
        registeredtype: typeInformation.registeredtype,
        displaytype: typeInformation.displaytype,
        typeLabel: '',
        iconName: undefined,
        providerType,
      };
      getIconAndTypeLabel({
        localize,
        typeInformation: typeInfo,
        registeredTypes, // DSD+
        resolveIcon,
        getIconFromRegisteredType,
      });
      return typeInfo;
    });
};

export const attributeToString = (props: {
  attributeMap: SearchResult['attributeMap'];
  attributeName: string;
}): [boolean, string] => {
  let ok = false;
  let value = '';
  if (props.attributeMap?.[props.attributeName] !== undefined) {
    const propertyValue = props.attributeMap[props.attributeName];
    if (typeof propertyValue === 'string') {
      value = propertyValue;
      ok = true;
    } else if (propertyValue.stringValue && typeof propertyValue.stringValue === 'string') {
      value = propertyValue.stringValue as string;
      ok = true;
    }
  }
  return [ok, value];
};

export const attributeToBoolean = (props: {
  attributeMap: SearchResult['attributeMap'];
  attributeName: string;
}): [boolean, boolean] => {
  let ok = false;
  let value = false;
  if (props.attributeMap?.[props.attributeName] !== undefined) {
    const propertyValue = props.attributeMap[props.attributeName];
    if (typeof propertyValue === 'boolean') {
      value = propertyValue;
      ok = true;
    } else if (
      propertyValue.boolValue !== undefined &&
      typeof propertyValue.boolValue === 'boolean'
    ) {
      value = !!propertyValue.boolValue;
      ok = true;
    }
  }

  return [ok, value];
};

export const calculateTypeInformation = (item: {
  type: SearchResult['type'];
  attributeMap: SearchResult['attributeMap']; // Record<string,any> for catalog grpc type handling
  registeredtype?: SearchResult['registeredtype'];
  displaytype?: SearchResult['displaytype'];
  resourceUri?: SearchResult['resourceuri'];
}): EntityTypeInformation => {
  let displaytype;
  let registeredtype;
  let iscrawled = false;
  const entityType = item.type;

  if (item.registeredtype && item.registeredtype !== '') {
    registeredtype = item.registeredtype;
  }
  if (item.displaytype && item.displaytype !== '') {
    displaytype = item.displaytype;
  }
  // in 2022.11 the catalog service started added the ?ext= query parameter to the resource URI for all tables that
  // are NOT loaded into memory (DATAPREP-34793) because the sourceCaslib and sourceName properties are unreliable in certain
  // situations (i.e. a table has been loaded using the table.copy action), so (for cas only) we will use the presence of the
  // ext= to indicate that a table is not in-memory and otherwise assume that the table is in memory.
  // Since the change to catalog service was only expanding the number of cases where the ext= param is included, this code is safe
  // to run even with older catalog service implementations.
  if (determineProviderType(item.type) === 'cas') {
    const hasExt = (item.resourceUri || '').indexOf('ext=') > -1;
    // cas tables should always have a registeredType of dataTable
    registeredtype = 'dataTable';
    if (displaytype !== 'InMemoryTable') {
      // only set the datatype to InMemoryTable if we have both an resourceUri and the ext param is not specified
      if (item.resourceUri && !hasExt) {
        displaytype = 'InMemoryTable';
      }
    } else {
      // if a table has been identified as in-memory, but the resource URI is for an unloaded table (should be rare or never happen)
      // then unset the displayType and let the code below reassign the display type to the type corresponding to the source (if any)
      if (hasExt) {
        displaytype = undefined;
      }
    }
  }
  // the code below is still relevant for compute tables (providerType === 'sas') and tables that are errently defined as in-memory
  // but with the ext= param (should not really happen, but is technically possible)
  if (!displaytype || !registeredtype) {
    displaytype = entityType;
    registeredtype = entityType;
    if (entityType === 'reference') {
      const [refTypeOk, refType] = attributeToString({
        attributeMap: item.attributeMap,
        attributeName: 'referencedType',
      });
      if (refTypeOk) {
        registeredtype = refType;
        displaytype = registeredtype;
      }
    } else if (
      entityType === 'casTable' ||
      entityType === 'sasTable' ||
      entityType === 'casMemTable'
    ) {
      registeredtype = 'dataTable';
      displaytype = registeredtype;
      const [sourceSystemOk, sourceSystem] = attributeToString({
        attributeMap: item.attributeMap,
        attributeName: 'sourceSystem',
      });
      if (sourceSystemOk) {
        displaytype = sourceSystem;
      }

      // Is this an inmemory table? DATAPREP-23773
      const [loadedOk, isLoaded] = attributeToBoolean({
        attributeMap: item.attributeMap,
        attributeName: 'isLoaded',
      });
      const [sourceCaslibOk, sourceCaslib] = attributeToString({
        attributeMap: item.attributeMap,
        attributeName: 'sourceCaslib',
      });
      const [sourceNameOk, sourceName] = attributeToString({
        attributeMap: item.attributeMap,
        attributeName: 'sourceName',
      });
      if (
        loadedOk &&
        isLoaded &&
        sourceCaslibOk &&
        sourceCaslib !== '' &&
        sourceNameOk &&
        sourceName !== ''
      ) {
        displaytype = 'InMemoryTable';
      }
    }
  }

  if (registeredtype === 'dataTable') {
    const [analysisTimeStampOk, analysisTimeStamp] = attributeToString({
      attributeMap: item.attributeMap,
      attributeName: 'analysisTimeStamp',
    });
    if (analysisTimeStampOk && analysisTimeStamp !== '') {
      // is a dataTable AND has an analyzed date
      iscrawled = true;
    }
  }
  return { displaytype, registeredtype, iscrawled };
};

/**
 * checkLibraryOnSearchResult will check the provided search result for a library property, if that property is missing and the
 * search result is a cas table with a dataTables URI the library field will be populated by pulling the library information from
 * the dataTables URI
 * @param result - a search result object that may or may not be a dataTable without a populated library field
 */
export const checkLibraryOnSearchResult = (result?: {
  typeInformation: { registeredtype?: string; providerType?: string };
  resourceuri?: string;
  library?: string;
}) => {
  if (
    result &&
    !result.library &&
    result.typeInformation.registeredtype === 'dataTable' &&
    result.typeInformation.providerType === 'cas' &&
    result.resourceuri?.startsWith('/dataTables/')
  ) {
    const [ts, err] = ResolveTableURI(result.resourceuri);
    err &&
      logger.debug(
        `error while trying to pull library from uri ${result.resourceuri}. The error was ${err}`
      );
    result.library = ts?.dataSourceId ?? '';
  }
};

export const filterByDisplayType = (items: SearchResult[], wantedTypes: string[]) => {
  return items.filter((item) => {
    let found = false;
    for (const type of wantedTypes) {
      if (item.typeInformation.displaytype === type) {
        found = true;
        break;
      }
    }
    return found;
  });
};
